import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';

// Capacitor - iOS/Android native features
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';

// Initialize Capacitor plugins for native platforms
const initCapacitor = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Hide splash screen after app is ready
      await SplashScreen.hide();

      // Configure status bar
      await StatusBar.setStyle({ style: Style.Dark });
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
      }

      // Keyboard configuration
      Keyboard.addListener('keyboardWillShow', () => {
        document.body.classList.add('keyboard-visible');
      });
      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('keyboard-visible');
      });
    } catch (error) {
      console.warn('Capacitor init error:', error);
    }
  }
};

// Run initialization
initCapacitor();

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import Navbar from './components/Navbar';
import BottomNavigation from './components/BottomNavigation';
import HomePage from './pages/HomePage';
import HomePageRedirect from './components/HomePageRedirect';
import LoginPage from './pages/LoginPage';
import EmailAuthPage from './pages/EmailAuthPage';
import RegisterTypeSelectPage from './pages/RegisterTypeSelectPage';
import RegisterInfoPage from './pages/RegisterInfoPage';
import CheckEmailPage from './pages/CheckEmailPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import VerifyPage from './pages/VerifyPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import CreateJobPage from './pages/employer/CreateJob';
import ProfilePage from './pages/ProfilePage';
import ApplicationsPage from './pages/ApplicationsPage';
import AdminPage from './pages/AdminPage';
import JobSeekerManagePage from './pages/JobSeekerManagePage';
import AdminEmployersPage from './pages/AdminEmployersPage';
import EmployerManagePage from './pages/EmployerManagePage';
import AdminWalletManagement from './pages/AdminWalletManagement';
import AdminDashboard from './pages/AdminDashboard';
import MobileJobSearchPage from './pages/MobileJobSearchPage';
import EmployerPayments from './pages/EmployerPayments';
import AdminSettingsFees from './pages/AdminSettingsFees';
import MyPage from './pages/MyPage';
import SeekerDashboard from './pages/SeekerDashboard';
import AreaSearchPage from './pages/AreaSearchPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';
import ChatListPage from './pages/ChatListPage';
import ChatRoomPage from './pages/ChatRoomPage';
import MessagePage from './pages/MessagePage';
import EmployerProfileForm from './components/EmployerProfileForm';
import EmployerProfileDetail from './pages/EmployerProfileDetail';
import EmployerReviewsPage from './pages/EmployerReviewsPage';
import CategoryEmployerList from './pages/CategoryEmployerList';
import WalletManagement from './pages/WalletManagement';
import { GoogleAuthCallback, LineAuthCallback } from './pages/AuthCallback';
import AdminLogin from './pages/AdminLogin';
import DebugTest from './components/DebugTest';
import FavoritesPage from './pages/FavoritesPage';
import LocationSettingsPage from './pages/LocationSettingsPage';
import AdminBannerManagement from './pages/AdminBannerManagement';
import EmployerDashboard from './pages/EmployerDashboard';
import EmployerAccountSettings from './pages/employer/EmployerAccountSettings';
import EmployerJobManagement from './pages/employer/EmployerJobManagement';
import EmployerRecruitmentStatus from './pages/employer/RecruitmentStatus';
import EmployerJobDetail from './pages/employer/JobDetail';
import JobQRPage from './pages/employer/JobQRPage';
import TimesheetsPage from './pages/employer/TimesheetsPage';
import FinanceHistory from './pages/employer/FinanceHistory';
import EmployerPlanPage from './pages/employer/EmployerPlanPage';

// Error Pages
import { NotFoundPage, ForbiddenPage, ServerErrorPage, ErrorBoundary } from './pages/ErrorPages';
import EmployerWorkHistory from './pages/employer/EmployerWorkHistory';
import EmployerMessages from './pages/employer/EmployerMessages';
import EmployerReports from './pages/employer/EmployerReports';
import AdminRedirectGuard from './components/AdminRedirectGuard';
import AdminLayout from './components/AdminLayout';
import AdminFinance from './pages/admin/AdminFinance';
import AdminUserWithdrawals from './pages/admin/AdminUserWithdrawals';
import AdminIdentityVerificationPage from './pages/AdminIdentityVerificationPage';
import AdminWithdrawalsPage from './pages/AdminWithdrawalsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';

import JobSeekerProfileSetup from './pages/JobSeekerProfileSetup';
import EmployerProfileSetup from './pages/EmployerProfileSetup';
import EmployerCreditCardRegistration from './pages/employer/EmployerCreditCardRegistration';
import AddCardPage from './pages/employer/AddCardPage';
import JobManagementPage from './pages/JobManagementPage';
import AdjustTimesheet from './pages/AdjustTimesheet';
import ReviewNew from './pages/ReviewNew';

// MyPage Detail Pages
import AccountSettings from './pages/MyPageDetails/AccountSettings';
import WorkHistory from './pages/MyPageDetails/WorkHistory';
import FAQ from './pages/MyPageDetails/FAQ';
import PrivacyPolicy from './pages/MyPageDetails/PrivacyPolicy';
import Contact from './pages/MyPageDetails/Contact';
import ReviewsPenalties from './pages/MyPageDetails/ReviewsPenalties';
import Badges from './pages/MyPageDetails/Badges';
import Qualifications from './pages/MyPageDetails/Qualifications';
import Earnings from './pages/MyPageDetails/Earnings';
import TaxDocuments from './pages/MyPageDetails/TaxDocuments';
import LocationSettings from './pages/MyPageDetails/LocationSettings';
import PushNotifications from './pages/MyPageDetails/PushNotifications';
import TermsOfService from './pages/MyPageDetails/TermsOfService';
import BugReport from './pages/MyPageDetails/BugReport';
import MutedStores from './pages/MyPageDetails/MutedStores';
import EmailSettings from './pages/MyPageDetails/EmailSettings';
import HealthCheck from './pages/MyPageDetails/HealthCheck';
import JobCategories from './pages/MyPageDetails/JobCategories';
import EmployerAdsPage from './pages/employer/EmployerAdsPage';
import AdminAdsPage from './pages/admin/AdminAdsPage';
import AdminQualificationsPage from './pages/admin/AdminQualificationsPage';
import JoinTeam from './pages/MyPageDetails/JoinTeam';
import Withdrawal from './pages/MyPageDetails/Withdrawal';
import UserLedgerPage from './pages/UserLedgerPage';

// Work Pages
import WorkPage from './pages/WorkPage';
import UpcomingWorkPage from './pages/UpcomingWorkPage';
import WorkHistoryPage from './pages/WorkHistoryPage';
import BitersPage from './pages/BitersPage';
import QRCheckIn from './pages/QRCheckIn';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

import { registerPush } from './lib/push';



const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  // 直前の認証（メール認証後の自動ログイン等）では AuthContext の復元が遅延するため、
  // localStorage の存在で一時的に通過させる（ページ内で再同期する）
  const hasLocalAuth = (
    !!localStorage.getItem('token') || !!localStorage.getItem('admin_token') ||
    !!localStorage.getItem('user') || !!localStorage.getItem('admin_user')
  );

  if (loading && !hasLocalAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user || hasLocalAuth) return children;
  return <Navigate to="/login" replace />;
};

const JobSeekerLayout = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  // 求職者ログイン時は下部にパディングを追加
  const isJobSeeker = user && user.user_type === 'job_seeker';

  // 求職者向けページでBottomNavigationを表示するページのリスト
  const jobSeekerPagesWithBottomNav = [
    '/profile',           // プロフィールページ
    '/applications',      // 応募履歴ページ
    '/mypage',           // マイページ
    '/favorites',        // お気に入りページ
    '/chat',             // メッセージページ
    '/jobs',             // 求人一覧ページ
    '/mobile-search',    // 事業者検索ページ
    '/wallet-management', // ウォレット管理ページ
    '/seeker-dashboard', // 求職者ダッシュボード
    '/work'              // はたらくページ（新規追加）
  ];

  // 現在のパスが求職者向けページかどうかチェック
  const shouldShowBottomNav = isJobSeeker && (
    jobSeekerPagesWithBottomNav.some(path => location.pathname.startsWith(path))
  );

  return (
    <div className={shouldShowBottomNav ? 'pb-16' : ''}>
      {children}
      {shouldShowBottomNav && <BottomNavigation />}
    </div>
  );
};

const AdminProtectedRoute = ({ children }) => {
  const [isChecking, setIsChecking] = React.useState(true);
  const [isAuthorized, setIsAuthorized] = React.useState(false);

  React.useEffect(() => {
    const checkAdminAuth = () => {
      // 管理者専用ログインでのトークンをチェック
      const adminToken = localStorage.getItem('admin_token');
      const adminUser = localStorage.getItem('admin_user');

      // 統合認証システムでログインした管理者をチェック
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      let user = null;
      try {
        user = userStr ? JSON.parse(userStr) : null;
      } catch (e) {
        console.error('Failed to parse user data:', e);
      }

      console.log('AdminProtectedRoute - checking auth:', {
        adminToken: !!adminToken,
        adminUser: !!adminUser,
        token: !!token,
        user: !!user,
        userType: user?.user_type
      });

      // 管理者認証の判定を簡素化
      const isAdmin = (adminToken && adminUser) || (user && user.user_type === 'admin');

      console.log('AdminProtectedRoute - Auth result:', { isAdmin });

      setIsAuthorized(isAdmin);
      setIsChecking(false);
    };

    checkAdminAuth();

    // localStorage の変更をリスンする（短いインターバル）
    const handleStorageChange = () => {
      checkAdminAuth();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">認証確認中...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    console.log('AdminProtectedRoute - redirecting to login');
    return <Navigate to="/admin-login" replace />;
  }

  console.log('AdminProtectedRoute - access granted');
  return children;
};

// 事業者専用の保護されたルートコンポーネント
const EmployerProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // ローディング中は読み込み画面を表示
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.user_type !== 'employer') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// 求職者専用の保護されたルートコンポーネント
const JobSeekerProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  // AuthContextの復元前でも localStorage の認証痕跡があれば通す
  const hasLocalAuth = (
    !!localStorage.getItem('token') || !!localStorage.getItem('user')
  );

  if (loading && !hasLocalAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // ユーザーが読み込まれていなくても localStorage があれば一旦通過させる
  if (!user && hasLocalAuth) return children;

  if (!user) return <Navigate to="/login" replace />;

  if (user.user_type !== 'job_seeker') {
    return <Navigate to="/employer-dashboard" replace />;
  }

  return children;
};

function App() {
  useEffect(() => {
    registerPush();
  }, []);
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <div className="app">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  style: {
                    background: '#10B981',
                  },
                },
                error: {
                  style: {
                    background: '#EF4444',
                  },
                },
              }}
            />
            <Routes>
              {/* Error Pages */}
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="/403" element={<ForbiddenPage />} />
              <Route path="/500" element={<ServerErrorPage />} />

              {/* Admin Login Route - accessible without authentication */}
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Legacy admin routes - redirect to new structure */}
              <Route path="/admin-wallet" element={<Navigate to="/admin/wallet" replace />} />
              <Route path="/admin-dashboard" element={<Navigate to="/admin/dashboard" replace />} />

              {/* Admin routes (運営専用) - 個別に AdminLayout を適用してスコープを限定 */}
              <Route path="/admin/dashboard" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/wallet" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminWalletManagement />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/wallet-management" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminWalletManagement />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/banners" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminAdsPage />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/finance" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminFinance />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/user-withdrawals" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminUserWithdrawals />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/identity-verifications" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminIdentityVerificationPage />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/withdrawals" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminWithdrawalsPage />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />
              <Route path="/admin/settings" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminSettingsPage />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />


              <Route path="/admin/qualifications" element={
                <AdminLayout>
                  <AdminProtectedRoute>
                    <AdminQualificationsPage />
                  </AdminProtectedRoute>
                </AdminLayout>
              } />

              {/* Regular app routes - with navbar and admin redirect protection */}
              <Route path="*" element={
                <AdminRedirectGuard>
                  <div className="app-layout">
                    <Navbar />
                    <JobSeekerLayout>
                      <main>
                        <Routes>
                          <Route path="/" element={<HomePageRedirect />} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register/type" element={<RegisterTypeSelectPage />} />
                          <Route path="/register/info" element={<RegisterInfoPage />} />
                          <Route path="/auth/check-email" element={<CheckEmailPage />} />
                          <Route path="/auth/confirm" element={<ConfirmEmailPage />} />
                          <Route path="/verify" element={<VerifyPage />} />
                          <Route path="/terms" element={<TermsPage />} />
                          <Route path="/privacy" element={<PrivacyPage />} />
                          <Route path="/register-select" element={<Navigate to="/register/type" replace />} />
                          <Route path="/register-jobseeker" element={<RegisterTypeSelectPage userType="job_seeker" />} />
                          <Route path="/register" element={<EmailAuthPage />} />
                          <Route path="/job-seeker-profile-setup" element={<JobSeekerProfileSetup />} />
                          <Route path="/employer-profile-setup" element={<EmployerProfileSetup />} />
                          <Route path="/register-info" element={<RegisterInfoPage />} />
                          <Route path="/jobs/*" element={
                            <JobsPage />
                          } />
                          <Route path="/area-search" element={
                            <JobSeekerProtectedRoute>
                              <AreaSearchPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mobile-search" element={
                            <MobileJobSearchPage />
                          } />
                          <Route path="/mypage" element={
                            <JobSeekerProtectedRoute>
                              <MyPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/favorites" element={
                            <JobSeekerProtectedRoute>
                              <FavoritesPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/location-settings" element={
                            <JobSeekerProtectedRoute>
                              <LocationSettingsPage />
                            </JobSeekerProtectedRoute>
                          } />

                          {/* MyPage Detail Routes */}
                          <Route path="/mypage/account-settings" element={
                            <JobSeekerProtectedRoute>
                              <AccountSettings />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/reviews-penalties" element={
                            <JobSeekerProtectedRoute>
                              <ReviewsPenalties />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/badges" element={
                            <JobSeekerProtectedRoute>
                              <Badges />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/qualifications" element={
                            <JobSeekerProtectedRoute>
                              <Qualifications />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/work-history" element={
                            <JobSeekerProtectedRoute>
                              <WorkHistory />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/earnings" element={
                            <JobSeekerProtectedRoute>
                              <Earnings />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/tax-documents" element={
                            <JobSeekerProtectedRoute>
                              <TaxDocuments />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/location-settings" element={
                            <JobSeekerProtectedRoute>
                              <LocationSettings />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/push-notifications" element={
                            <JobSeekerProtectedRoute>
                              <PushNotifications />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/muted-stores" element={
                            <JobSeekerProtectedRoute>
                              <MutedStores />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/email-settings" element={
                            <JobSeekerProtectedRoute>
                              <EmailSettings />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/health-check" element={
                            <JobSeekerProtectedRoute>
                              <HealthCheck />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/faq" element={
                            <JobSeekerProtectedRoute>
                              <FAQ />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/privacy-policy" element={
                            <JobSeekerProtectedRoute>
                              <PrivacyPolicy />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/job-categories" element={
                            <JobSeekerProtectedRoute>
                              <JobCategories />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/terms" element={
                            <JobSeekerProtectedRoute>
                              <TermsOfService />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/bug-report" element={
                            <JobSeekerProtectedRoute>
                              <BugReport />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/contact" element={
                            <JobSeekerProtectedRoute>
                              <Contact />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/join-team" element={
                            <JobSeekerProtectedRoute>
                              <JoinTeam />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/withdrawal" element={
                            <JobSeekerProtectedRoute>
                              <Withdrawal />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/mypage/wallet-history" element={
                            <JobSeekerProtectedRoute>
                              <UserLedgerPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/seeker-dashboard" element={
                            <JobSeekerProtectedRoute>
                              <SeekerDashboard />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/identity-verification" element={
                            <JobSeekerProtectedRoute>
                              <IdentityVerificationPage />
                            </JobSeekerProtectedRoute>
                          } />

                          {/* Work Pages */}
                          <Route path="/work" element={
                            <JobSeekerProtectedRoute>
                              <WorkPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/work/qr" element={
                            <JobSeekerProtectedRoute>
                              <QRCheckIn />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/work/upcoming" element={
                            <JobSeekerProtectedRoute>
                              <UpcomingWorkPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/work/history" element={
                            <JobSeekerProtectedRoute>
                              <WorkHistoryPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/work/history/:id/adjust" element={
                            <JobSeekerProtectedRoute>
                              <AdjustTimesheet />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/reviews/new" element={
                            <ProtectedRoute>
                              <ReviewNew />
                            </ProtectedRoute>
                          } />
                          <Route path="/work/biters" element={
                            <JobSeekerProtectedRoute>
                              <BitersPage />
                            </JobSeekerProtectedRoute>
                          } />

                          <Route path="/chat" element={
                            <JobSeekerProtectedRoute>
                              <ChatListPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/chat/:roomId" element={
                            <JobSeekerProtectedRoute>
                              <ChatRoomPage />
                            </JobSeekerProtectedRoute>
                          } />
                          <Route path="/messages/:applicationId" element={
                            <ProtectedRoute>
                              <MessagePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/jobs/:id" element={<JobDetailPage />} />
                          <Route path="/create-job" element={
                            <EmployerProtectedRoute>
                              <CreateJobPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/job-management" element={
                            <EmployerProtectedRoute>
                              <JobManagementPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/profile" element={
                            <ProtectedRoute>
                              <ProfilePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/applications" element={
                            <EmployerProtectedRoute>
                              <ApplicationsPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/admin" element={
                            <ProtectedRoute>
                              <AdminPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/job-seekers" element={
                            <ProtectedRoute>
                              <JobSeekerManagePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/employers" element={
                            <ProtectedRoute>
                              <AdminEmployersPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/settings/fees" element={
                            <ProtectedRoute>
                              <AdminSettingsFees />
                            </ProtectedRoute>
                          } />
                          <Route path="/employer-dashboard" element={
                            <EmployerProtectedRoute>
                              <EmployerDashboard />
                            </EmployerProtectedRoute>
                          } />
                          {/* 事業者ダッシュボードのエイリアス */}
                          <Route path="/business/dashboard" element={
                            <EmployerProtectedRoute>
                              <EmployerDashboard />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/account-settings" element={
                            <EmployerProtectedRoute>
                              <EmployerAccountSettings />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/job-management" element={
                            <EmployerProtectedRoute>
                              <EmployerJobManagement />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/recruitment-status" element={
                            <EmployerProtectedRoute>
                              <EmployerRecruitmentStatus />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/timesheets" element={
                            <EmployerProtectedRoute>
                              <TimesheetsPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/finance" element={
                            <EmployerProtectedRoute>
                              <FinanceHistory />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/job/:jobId" element={
                            <EmployerProtectedRoute>
                              <EmployerJobDetail />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/job/:jobId/qr" element={
                            <EmployerProtectedRoute>
                              <JobQRPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/business/add-card" element={
                            <EmployerProtectedRoute>
                              <AddCardPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/work-history" element={
                            <EmployerProtectedRoute>
                              <EmployerWorkHistory />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/messages" element={
                            <EmployerProtectedRoute>
                              <EmployerMessages />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/messages/:workerId" element={
                            <EmployerProtectedRoute>
                              <EmployerMessages />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/reports" element={
                            <EmployerProtectedRoute>
                              <EmployerReports />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/plan" element={
                            <EmployerProtectedRoute>
                              <EmployerPlanPage />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer/credit-card" element={
                            <EmployerProtectedRoute>
                              <EmployerCreditCardRegistration />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer-manage" element={<Navigate to="/employer-dashboard" replace />} />
                          <Route path="/employer/payments" element={
                            <EmployerProtectedRoute>
                              <EmployerPayments />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer-profile/create" element={
                            <EmployerProtectedRoute>
                              <EmployerProfileForm />
                            </EmployerProtectedRoute>
                          } />
                          <Route path="/employer-profile/edit" element={
                            <ProtectedRoute>
                              <EmployerProfileForm />
                            </ProtectedRoute>
                          } />
                          <Route path="/employer-profile/:profileId" element={<EmployerProfileDetail />} />
                          <Route path="/employer-profile/:profileId/reviews" element={<EmployerReviewsPage />} />
                          <Route path="/category/:categoryName" element={<CategoryEmployerList />} />
                          <Route path="/categories" element={<CategoryEmployerList />} />
                          <Route path="/wallet-management" element={
                            <ProtectedRoute>
                              <WalletManagement />
                            </ProtectedRoute>
                          } />
                          {/* OAuth callback routes */}
                          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
                          <Route path="/auth/line/callback" element={<LineAuthCallback />} />
                          {/* Debug route */}
                          <Route path="/debug" element={<DebugTest />} />
                          {/* Catch-all route for undefined paths */}
                          <Route path="*" element={<Navigate to="/404" replace />} />
                        </Routes>
                      </main>
                    </JobSeekerLayout>
                  </div>
                </AdminRedirectGuard>
              } />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;